/*  ========================================================================
    # Components - Leporello Months Slider
    ========================================================================  */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useLocalization from '../../hooks/useLocalization';
import useBreakpoints from '../../hooks/useBreakpoints';
import { breakpoints } from '../../helpers/breakpoints';
import useTranslation from '../../hooks/useTranslation';

const LeporelloMonthsSlider = ({
  months,
  selectedKey,
  selectedMonth,
  selectedYear,
  onMonthChange
}) => {
  const activeMonth = {
    key: selectedKey,
    date: selectedMonth + selectedYear
  };
  const [sliderTranslation, setSliderTranslation] = useState(0);
  const slideRef = useRef(null);
  const monthRef = useRef([]);
  const { smDown } = useBreakpoints();
  const _ = useTranslation();

  // Icons
  const local = useLocalization();
  const iconChevronLeft = `${local('svgSprite')}#chevron-left`;
  const iconChevronRight = `${local('svgSprite')}#chevron-right`;

  const postitionSlide = key => {
    setSliderTranslation(slideRef.current.clientWidth * key);
  };

  const prevMonth = e => {
    if (activeMonth.key > 0) {
      const newKey = activeMonth.key - 1;
      onMonthChange(
        { month: months[newKey].month, year: months[newKey].year, monthKey: newKey },
        e
      );

      if (smDown && newKey < 8) {
        postitionSlide(newKey);
      }
    }
  };

  const nextMonth = e => {
    if (activeMonth.key < months.length - 1) {
      const newKey = activeMonth.key + 1;

      onMonthChange(
        { month: months[newKey].month, year: months[newKey].year, monthKey: newKey },
        e
      );

      if (smDown && newKey > 3 && newKey <= 7) {
        postitionSlide(newKey);
      }
    }
  };

  const handleMonthClick = date => {
    if (activeMonth !== date) {
      onMonthChange({ month: date.month, year: date.year, monthKey: date.key });
    }
    if (smDown && date.key >= 7) {
      postitionSlide(7);
    } else if (smDown && date.key > 3 && date.key < 7) {
      postitionSlide(date.key);
    }
  };

  /**
   * Handle keyboard navigation for month selection
   */
  const handleMonthKeyDown = (e, month, year, key) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMonthClick({ month, year, key });
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (key > 0) {
        monthRef.current[key].tabIndex = -1;
        monthRef.current[key - 1].tabIndex = 0;
        monthRef.current[key - 1].focus();
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (key < monthRef.current.length - 1) {
        monthRef.current[key].tabIndex = -1;
        monthRef.current[key + 1].tabIndex = 0;
        monthRef.current[key + 1].focus();
      }
    } else if (e.key === 'Tab') {
      if (activeMonth.key !== key) {
        monthRef.current[key].tabIndex = -1;
        monthRef.current[activeMonth.key].tabIndex = 0;
      }
    }
  };

  useEffect(() => {
    if (smDown && selectedKey >= 7) {
      postitionSlide(7);
    } else if (smDown && selectedKey > 3 && selectedKey <= 7) {
      postitionSlide(selectedKey);
    }

    window.addEventListener(
      'resize',
      () => {
        if (window.outerWidth > breakpoints.md) {
          postitionSlide(0);
        } else {
          handleMonthClick(activeMonth);
        }
      },
      { passive: true }
    );
  });

  return (
    <div className='c-leporello-months-slider'>
      <button
        type='button'
        className='c-leporello-months-slider__controls-left c-button c-button--icon-sm'
        onClick={e => {
          prevMonth(e);
        }}
        aria-label={_('leporello_prev')}
        disabled={activeMonth.key === 0}
      >
        <svg viewBox='0 0 200 200'>
          <use href={iconChevronLeft}></use>
        </svg>
      </button>
      <div className='c-leporello-months-slider__wrapper'>
        <div
          className='c-leporello-months-slider__items'
          style={{ transform: `translateX(-${sliderTranslation}px)` }}
        >
          {months.map((date, key) => (
            <div className='c-leporello-months-slider__month' key={key} ref={slideRef}>
              <button
                type='button'
                className={`
                  c-label
                  ${activeMonth.key === key ? 'c-label--active' : ''}
                `}
                onClick={() => {
                  handleMonthClick({ month: date.month, year: date.year, key });
                }}
                onKeyDown={e => {
                  handleMonthKeyDown(e, date.month, date.year, key);
                }}
                key={key}
                aria-label={date.monthName}
                tabIndex={activeMonth.key === key ? 0 : -1}
                ref={el => {
                  monthRef.current[key] = el;
                }}
              >
                {date.monthName}
              </button>
            </div>
          ))}
        </div>
      </div>
      <button
        type='button'
        className='c-leporello-months-slider__controls-right c-button c-button--icon-sm'
        onClick={e => {
          nextMonth(e);
        }}
        aria-label={_('leporello_next')}
        disabled={activeMonth.key === months.length - 1}
      >
        <svg viewBox='0 0 200 200'>
          <use href={iconChevronRight}></use>
        </svg>
      </button>
    </div>
  );
};

LeporelloMonthsSlider.propTypes = {
  months: PropTypes.array,
  selectedKey: PropTypes.number,
  onMonthChange: PropTypes.func,
  selectedMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default LeporelloMonthsSlider;
